import { ApiRoutes } from "@/enums/apiRoutes.enum";
import axios from "@/lib/axios";
import {
  Basket,
  Category,
  ProductDemographics,
  ProductGroup,
  ProductGroupWrite,
  ProductPeak,
  ProductsPage,
  ProductTarget,
  ProductTotals,
  WindowKey,
} from "@/types/stats";

const withQuery = (
  route: string,
  params: Record<string, string | number | boolean | undefined>,
) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `${route}?${qs}` : route;
};

/**
 * Products stats API (v2) — documentation/features/02-products-stats.md.
 * All calls go same-origin through the BFF proxy
 * (src/pages/api/v2/[...slug].ts) which injects the bearer token.
 */
export const productsService = {
  /** Paginated catalog; search = case-insensitive substring on naziv or sifra. LIVE. */
  async getProducts(
    page = 1,
    pageSize = 50,
    search?: string,
    categoryIds?: string[],
  ): Promise<ProductsPage> {
    const response = await axios.get<ProductsPage>(
      withQuery(ApiRoutes.STATS_PRODUCTS, {
        page,
        pageSize,
        search: search || undefined,
        category:
          categoryIds && categoryIds.length
            ? categoryIds.join(",")
            : undefined,
      }),
    );
    return response.data;
  },

  /** All product categories. LIVE. */
  async getCategories(): Promise<Category[]> {
    const response = await axios.get<Category[]>(ApiRoutes.STATS_CATEGORIES);
    return response.data;
  },

  // Custom product groups — shared with the campaigns audience builder.

  async getProductGroups(): Promise<ProductGroup[]> {
    const response = await axios.get<ProductGroup[]>(
      ApiRoutes.STATS_PRODUCT_GROUPS,
    );
    return response.data;
  },

  async createProductGroup(payload: ProductGroupWrite): Promise<void> {
    await axios.post(ApiRoutes.STATS_PRODUCT_GROUPS, payload);
  },

  async updateProductGroup(
    id: number,
    payload: Partial<ProductGroupWrite>,
  ): Promise<void> {
    await axios.put(`${ApiRoutes.STATS_PRODUCT_GROUPS}/${id}`, payload);
  },

  async deleteProductGroup(id: number): Promise<void> {
    await axios.delete(`${ApiRoutes.STATS_PRODUCT_GROUPS}/${id}`);
  },

  /** Distinct-buyer split by gender/age/neighbourhood/rank. ROLLUP. */
  async getDemographics(
    target: ProductTarget,
    id: string,
    window: WindowKey,
  ): Promise<ProductDemographics> {
    const response = await axios.get<ProductDemographics>(
      withQuery(ApiRoutes.STATS_PRODUCTS_DEMOGRAPHICS, { target, id, window }),
    );
    return response.data;
  },

  /** Hour-of-day purchase distribution. LIVE. category/group clamped to ≤90d. */
  async getPeak(
    target: ProductTarget,
    id: string,
    window: WindowKey,
  ): Promise<ProductPeak> {
    const response = await axios.get<ProductPeak>(
      withQuery(ApiRoutes.STATS_PRODUCTS_PEAK, { target, id, window }),
    );
    return response.data;
  },

  /** Units sold + revenue for the selector over the window. LIVE. */
  async getTotals(
    target: ProductTarget,
    id: string,
    window: WindowKey,
  ): Promise<ProductTotals> {
    const response = await axios.get<ProductTotals>(
      withQuery(ApiRoutes.STATS_PRODUCTS_TOTALS, { target, id, window }),
    );
    return response.data;
  },

  /** "Bought together" for one SKU, lift-ranked. ROLLUP. limit capped at 50. */
  async getBasket(
    sifra: string,
    window: WindowKey,
    limit = 10,
  ): Promise<Basket> {
    const response = await axios.get<Basket>(
      withQuery(ApiRoutes.STATS_PRODUCTS_BASKET, { sifra, window, limit }),
    );
    return response.data;
  },
};
